import { Component, OnInit, ViewChild } from '@angular/core';
import {
  MessageService,
} from 'primeng/api';
import { TableDataComponent } from 'src/app/cms-v2/entities/table-data/components/table-data/table-data.component';
import { DataService } from 'src/app/services/data.service';
import { EntityViewService } from 'src/app/services/entity-view.service';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from 'src/app/auth/auth.service';
import { environment } from 'src/environments/environment';
import { LoggerService } from 'src/app/common/services/logger.service';

interface UserData {
  name: string;
  email: string;
  id: string;
  timestamp: Date;
  environment: string;
  editSource: string;
}

@Component({
  selector: 'app-asset-version-table',
  templateUrl: './asset-version-table.component.html',
  styleUrls: ['./asset-version-table.component.sass'],
})
export class AssetVersionTableComponent extends TableDataComponent implements OnInit {
  @ViewChild('spawnerTable') table: any;
  pageTitle: string = 'Economy Table';
  entity: any;
  id: any;
  type: any;
  tableType: any;
  filtered: any;
  sortableChallenges: any = [];
  urlQueryParams: any = {};
  displayUpdateModal: boolean = false;
  rowToUpdate: any;
  permission: boolean = false;

  constructor(
    private dataService: DataService,
    private messageService: MessageService,
    private route: ActivatedRoute,
    private entityViewService: EntityViewService,
    public authService: AuthService,
    private loggerService: LoggerService,
  ) {
    super();
  }

  async ngOnInit() {
    this.isLoading = true;
    
    const routeParams = this.route.snapshot.paramMap;
    this.id = Number(routeParams.get('id'));
    this.tableType = String(routeParams.get('table'));
    this.type = String(routeParams.get('type'));
    console.log(this.id, this.tableType, this.type)

    await this.entityViewService
    .getEntity('miscellaneous-build', { query: { id: this.id } })
    .then(async (data: any) => {
      this.entity = data;
    });
    
    console.log(this.entity)
    await this.filterUniques()
    this.filtered = this.filtered.reverse();
    this.isLoading = false;
  }

  /**
   * filters all the unique values for hashes and sorts by latest one
   */
  async filterUniques() {
    if(this.type == 'asset') {
      this.filtered = this.entity.asset_versions.filter((value:any, index:any, self:any) => {
        return self.findIndex((v:any) => v.destinationPath === value.destinationPath) === index;
      })
    } else if (this.type == 'image') {
      this.filtered = this.entity.image_versions.filter((value:any, index:any, self:any) => {
        return self.findIndex((v:any) => v.destinationPath === value.destinationPath) === index;
      })
    }
  }
  /**
   * Displays the confirmation modal and sets the row to be updated
   * @param row selected row
   */
  displayConfirmationModal(row: any) {
    this.displayUpdateModal = true;
    this.rowToUpdate = row;
  }

  /**
   * Creates userData object from current user information.
   * Validates user structure before accessing properties to prevent runtime errors.
   * 
   * @returns UserData object with user information or fallback values
   */
  private createUserData(): UserData {
    const currentUser = this.authService.getSocialUser();
    
    // Validate currentUser structure before accessing properties
    if (!currentUser || typeof currentUser !== 'object') {
      this.loggerService.log('Warning: currentUser is invalid, using fallback values');
      return {
        name: 'Unknown',
        email: 'unknown@example.com',
        id: '',
        timestamp: new Date(),
        environment: environment.env || 'unknown',
        editSource: 'asset-version-table'
      };
    }

    const user = currentUser.currentUser;
    if (!user || typeof user !== 'object') {
      this.loggerService.log('Warning: currentUser.currentUser is invalid, using fallback values');
      return {
        name: 'Unknown',
        email: 'unknown@example.com',
        id: '',
        timestamp: new Date(),
        environment: environment.env || 'unknown',
        editSource: 'asset-version-table'
      };
    }

    return {
      name: user.name || 'Unknown',
      email: user.email || 'unknown@example.com',
      id: user.id || '',
      timestamp: new Date(),
      environment: environment.env || 'unknown',
      editSource: 'asset-version-table'
    };
  }

  /**
   * Updates the version for the specified type (asset or image).
   * Consolidates shared logic to reduce code duplication.
   * 
   * @param versionType - Type of version to update ('asset' or 'image')
   * @param versionsArray - The versions array to update (asset_versions or image_versions)
   * @returns Promise resolving to success status
   */
  private async updateVersionForType(versionType: 'asset' | 'image', versionsArray: any[]): Promise<boolean> {
    try {
      const userData = this.createUserData();
      
      this.rowToUpdate.promotedAt = new Date().toISOString();
      this.rowToUpdate.userData = userData;
      versionsArray.push(this.rowToUpdate);

      this.loggerService.log(`Updating ${versionType} version for entity ${this.id}`, {
        versionType,
        entityId: this.id,
        userData: userData.name || userData.email
      });

      await this.dataService.updateRecord(this.id, 'miscellaneous-build', this.entity);
      
      return true;
    } catch (error: any) {
      const errorMessage = error?.message || error?.error?.message || 'Unknown error occurred';
      this.loggerService.log(`Error updating ${versionType} version`, {
        versionType,
        entityId: this.id,
        error: errorMessage,
        errorDetails: error
      });

      this.messageService.add({
        severity: 'error',
        summary: 'Error Updating Version',
        detail: `Failed to update ${versionType} version: ${errorMessage}`,
        sticky: true
      });

      throw error;
    }
  }

  /**
   * Updates the version of the asset/image.
   * Includes userData for audit trail and traceability.
   * 
   * @returns Promise resolving when update is complete
   */
  async updateVersion(): Promise<void> {
    try {
      if (this.type === 'asset') {
        await this.updateVersionForType('asset', this.entity.asset_versions);
      } else if (this.type === 'image') {
        await this.updateVersionForType('image', this.entity.image_versions);
      } else {
        throw new Error(`Invalid type: ${this.type}. Expected 'asset' or 'image'`);
      }

      this.displayUpdateModal = false;
    } catch (error) {
      // Error already handled in updateVersionForType, but keep modal open on error
      // so user can retry or see the error message
      this.loggerService.log('Error in updateVersion', error);
    }
  }
  /**
   * Transition update function with user feedback.
   * Handles success/error states and refreshes the view.
   */
  async updateMidVersion(): Promise<void> {
    try {
      await this.updateVersion();
      
      this.messageService.add({
        sticky: true,
        severity: 'success',
        summary: 'Successfully Updated The Version',
        detail: 'Successfully updated to the selected version',
      });

      // Refresh the view to show updated data
      await this.ngOnInit();
    } catch (error) {
      // Error handling is done in updateVersion, but log here for additional context
      this.loggerService.log('Error in updateMidVersion', error);
      // Don't show duplicate error message as updateVersion already shows one
    }
  }
}
